import { useState } from 'react'
import * as Sentry from '@sentry/react'

import { BaseError } from '../util/errors/BaseError'

const PERSISTED_STATE_KEY = ['NT_TOKEN', 'NT_CREDS', 'NT_TOOLS', 'NT_GATE_FEED_TOOLS'] as const

type PersistedStateKey = (typeof PERSISTED_STATE_KEY)[number]

export const getPersistedState = <T>(key: PersistedStateKey): T | null => {
  try {
    const item = window.localStorage.getItem(key)
    if (!item) {
      return null
      // throw new BaseError({
      //     cause: new Error(`Invalid Persisted State Key ${key}`)
      // })
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return JSON.parse(item) as T
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('Error loading persisted state', error)
    Sentry.captureException(`Error loading persisted state: ${error}`)
    return null
  }
}

export const setPersistedState = (key: PersistedStateKey, value: string): void => {
  window.localStorage.setItem(key, value)
}

export const usePersistedState = <T>(
  key: PersistedStateKey,
  initialValue?: T
): {
  value: T | null
  setValue: (nextValue: T | null) => void
  error: BaseError | null
} => {
  const [value, setStoredValue] = useState<T | null>(() => {
    const item = getPersistedState<T>(key)
    if (!item) return initialValue || null
    return item
  })

  const [error, setError] = useState<null | BaseError>(null)

  const setValue = (nextValue: T | ((val: T | null) => T) | null): void => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = nextValue instanceof Function ? nextValue(value) : nextValue
      // Save state
      setStoredValue(valueToStore)
      setPersistedState(key, JSON.stringify(valueToStore))
    } catch (e) {
      const parsedError = new BaseError({
        cause: new Error(`Error parsing stored value ${key}`),
      })
      Sentry.captureException(parsedError)
      setError(parsedError)
    }
  }

  return {
    value,
    error,
    setValue,
  }
}
